import { makeAutoObservable, runInAction } from 'mobx';
import { activeUsersWebSocketService } from 'services/activeUsersWebSocketService';

interface RoomEditingUser {
  username: string;
  joined_at: string;
}

interface RoomEditingState {
  roomId: string;
  userCount: number;
  users: RoomEditingUser[];
}

class RoomEditingStore {
  // Map of roomId to room editing state
  roomEditingStates: Map<string, RoomEditingState> = new Map();
  isConnected: boolean = false;
  connectionError: string | null = null;

  constructor() {
    makeAutoObservable(this);
    this.initializeWebSocket();
  }

  /**
   * Initialize WebSocket connection and message handlers
   */
  private initializeWebSocket = () => {
    // Connect to active users WebSocket
    activeUsersWebSocketService.connect();
    
    // Add message handler
    activeUsersWebSocketService.addMessageHandler(this.handleWebSocketMessage);
    
    // Update connection status
    this.updateConnectionStatus();
  };

  /**
   * Update connection status from WebSocket service
   */
  private updateConnectionStatus = () => {
    runInAction(() => {
      this.isConnected = activeUsersWebSocketService.isConnected;
      this.connectionError = activeUsersWebSocketService.connectionError;
    });
  };

  /**
   * Handle WebSocket messages from active users service
   */
  private handleWebSocketMessage = (message: any) => {
    this.updateConnectionStatus();
    
    switch (message.type) {
      case 'initial_active_users_state':
      case 'active_users_state':
      case 'active_users_update':
        this.updateFromActiveUsersData(message.data.activeUsersByRoom);
        break;
      default:
        // Handle other message types if needed
        break;
    }
  };

  /**
   * Update room editing states from active users data
   */
  private updateFromActiveUsersData = (activeUsersByRoom: any) => {
    runInAction(() => {
      // Clear existing states
      this.roomEditingStates.clear();
      
      // Update with new data
      Object.entries(activeUsersByRoom).forEach(([roomId, roomData]: [string, any]) => {
        const users: RoomEditingUser[] = Object.values(roomData.users || {});
        
        this.roomEditingStates.set(roomId, {
          roomId,
          userCount: roomData.userCount || 0,
          users
        });
      });
    });
  };

  /**
   * Update room editing state with user count and user list
   * @param roomId - The room ID
   * @param userCount - Number of users editing the room
   * @param users - List of users editing the room
   */
  updateRoomEditingState = (roomId: string, userCount: number, users: RoomEditingUser[]) => {
    runInAction(() => {
      this.roomEditingStates.set(roomId, {
        roomId,
        userCount,
        users
      });
    });
  };

  /**
   * Get room editing state for a specific room
   * @param roomId - The room ID
   * @returns Room editing state or null if not found
   */
  getRoomEditingState = (roomId: string): RoomEditingState | null => {
    return this.roomEditingStates.get(roomId) || null;
  };

  /**
   * Get user count for a specific room
   * @param roomId - The room ID
   * @returns Number of users editing the room
   */
  getRoomUserCount = (roomId: string): number => {
    const state = this.getRoomEditingState(roomId);
    return state ? state.userCount : 0;
  };

  /**
   * Get user list for a specific room
   * @param roomId - The room ID
   * @returns List of users editing the room
   */
  getRoomUsers = (roomId: string): RoomEditingUser[] => {
    const state = this.getRoomEditingState(roomId);
    return state ? state.users : [];
  };

  /**
   * Check if a specific user is editing a room
   * @param roomId - The room ID
   * @param username - The username to check
   * @returns True if user is editing the room
   */
  isUserEditingRoom = (roomId: string, username: string): boolean => {
    const users = this.getRoomUsers(roomId);
    return users.some(user => user.username === username);
  };

  /**
   * Clear room editing state for a specific room
   * @param roomId - The room ID
   */
  clearRoomEditingState = (roomId: string) => {
    runInAction(() => {
      this.roomEditingStates.delete(roomId);
    });
  };

  /**
   * Clear all room editing states
   */
  clearAllRoomEditingStates = () => {
    runInAction(() => {
      this.roomEditingStates.clear();
    });
  };

  /**
   * Get all rooms with active editors
   * @returns Array of room IDs that have active editors
   */
  getRoomsWithActiveEditors = (): string[] => {
    const roomsWithEditors: string[] = [];
    this.roomEditingStates.forEach((state, roomId) => {
      if (state.userCount > 0) {
        roomsWithEditors.push(roomId);
      }
    });
    return roomsWithEditors;
  };

  /**
   * Get total number of users editing across all rooms
   * @returns Total number of users editing
   */
  getTotalEditingUsers = (): number => {
    let total = 0;
    this.roomEditingStates.forEach(state => {
      total += state.userCount;
    });
    return total;
  };

  /**
   * Get connection status
   * @returns Connection status string
   */
  getConnectionStatus = (): string => {
    return activeUsersWebSocketService.getConnectionStatus();
  };

  /**
   * Manually refresh active users data
   */
  refreshActiveUsers = () => {
    activeUsersWebSocketService.getActiveUsers();
  };

  /**
   * Reconnect to WebSocket
   */
  reconnect = () => {
    activeUsersWebSocketService.disconnect();
    setTimeout(() => {
      activeUsersWebSocketService.connect();
    }, 1000);
  };

  /**
   * Cleanup when store is destroyed
   */
  cleanup = () => {
    activeUsersWebSocketService.removeMessageHandler(this.handleWebSocketMessage);
  };
}

export default RoomEditingStore;
